import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { frenchDate, frenchDateTime } from "@/lib/shariti";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert, RefreshCw, Clock, CheckCircle2, XCircle, MapPin, Flag, UserRound, ScrollText,
  Pencil, CalendarPlus, Ban, Lock, Layers, ClipboardList, ArrowRight,
} from "lucide-react";

// ============================================================================
// Journal des exceptions — deux registres complémentaires (aucune nouvelle
// entrée de navigation : tout vit dans /exceptions) :
//   1. « Rebookings forcés »  → rotation / contrainte Paris contournées.
//   2. « Heures (admin) »      → saisies rétroactives, corrections (surcouche)
//      et annulations tracées du module Pointage & Heures (qui / quand / motif,
//      avant → après). Le brut GPS reste immuable ; seule la valeur retenue est
//      corrigée. Une annulation ne supprime jamais physiquement le pointage.
// ============================================================================

const MODES = [
  { key: "rebookings", label: "Rebookings forcés", Icon: Flag },
  { key: "heures", label: "Heures (admin)", Icon: ClipboardList },
];

const fmtDateTime = (iso) => (iso ? frenchDate(String(iso).slice(0, 10)) : "—");
const fmtH = (h) => (h == null ? "—" : `${Number(h).toFixed(2).replace(".", ",")} h`);

// ---------------------------------------------------------------------------
// Registre 1 — Rebookings forcés (inchangé)
// ---------------------------------------------------------------------------
const REBOOK_TABS = [
  { key: "all", label: "Toutes" },
  { key: "pending", label: "En attente" },
  { key: "validated", label: "Validées" },
  { key: "refused", label: "Refusées" },
];

const RES = {
  pending: { label: "En attente", cls: "border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.28)] text-[#8A6D00]", Icon: Clock },
  validated: { label: "Validée", cls: "border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.1)] text-[#1F7A44]", Icon: CheckCircle2 },
  refused: { label: "Refusée", cls: "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] text-[#9B2226]", Icon: XCircle },
};

const REBOOK_TYPE = {
  rotation: { label: "Rotation", cls: "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] text-[#9B2226]", Icon: RefreshCw },
  paris: { label: "Contrainte Paris", cls: "border-[rgba(124,58,237,0.4)] bg-[rgba(124,58,237,0.08)] text-[#5B21B6]", Icon: MapPin },
};

function ResolutionBadge({ resolution }) {
  const r = RES[resolution] || RES.pending;
  const Icon = r.Icon;
  return (
    <span data-testid={`journal-res-${resolution}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${r.cls}`}>
      <Icon className="h-3 w-3" /> {r.label}
    </span>
  );
}

function RebookTypeBadge({ type }) {
  const t = REBOOK_TYPE[type] || REBOOK_TYPE.rotation;
  const Icon = t.Icon;
  return (
    <span data-testid={`journal-type-${type || "rotation"}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.cls}`}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

function RebookingsJournal() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, validated: 0, refused: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/planning/exceptions-journal");
      setRows(data.rows || []);
      setCounts(data.counts || { all: 0, pending: 0, validated: 0, refused: 0 });
    } catch (_) {
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.resolution === tab)),
    [rows, tab]
  );

  return (
    <div data-testid="rebookings-journal">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="journal-tabs">
          {REBOOK_TABS.map((t) => {
            const active = tab === t.key;
            const n = t.key === "all" ? counts.all : counts[t.key];
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`journal-tab-${t.key}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? "border-[var(--shariti-ink)] bg-[var(--shariti-ink)] text-white"
                    : "border-[var(--border-soft)] bg-white/70 text-ink hover:bg-white"}`}>
                {t.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/25" : "bg-[var(--surface-2)]"}`}>
                  {n ?? 0}
                </span>
              </button>
            );
          })}
        </div>
        <Button variant="outline" onClick={load} data-testid="journal-refresh" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Actualiser
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
        {loading ? (
          <div className="space-y-2 p-4" data-testid="journal-loading">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center" data-testid="journal-empty">
            <ShieldAlert className="h-8 w-8 text-muted-ink" />
            <p className="text-sm font-semibold text-ink">Aucune exception dans cette catégorie</p>
            <p className="text-xs text-muted-ink">Les rebookings forcés apparaîtront ici dès qu'un CDP en créera un.</p>
          </div>
        ) : (
          <Table data-testid="journal-table">
            <TableHeader>
              <TableRow>
                <TableHead>Emplacement</TableHead>
                <TableHead>Ligne · Asso</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Forcé par</TableHead>
                <TableHead>Motif du forçage</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Traité par</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.booking_id} data-testid={`journal-row-${r.booking_id}`}>
                  <TableCell className="max-w-[220px]">
                    <div className="flex items-center gap-1.5 font-semibold text-ink">
                      <Flag className="h-3.5 w-3.5 shrink-0 text-[#D64545]" />
                      <span className="break-words">{r.spot_name || "—"}</span>
                    </div>
                    {r.ville && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-muted-ink">
                        <MapPin className="h-3 w-3" />{r.ville}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px]">
                    <div className="font-semibold text-ink">{r.ligne || "—"}</div>
                    <div className="text-[11px] text-muted-ink">{r.association_acronym || "—"}</div>
                  </TableCell>
                  <TableCell><RebookTypeBadge type={r.forced_type} /></TableCell>
                  <TableCell className="whitespace-nowrap text-[12px] font-semibold">{frenchDate(r.date)}</TableCell>
                  <TableCell className="text-[12px]">
                    <div className="inline-flex items-center gap-1 font-semibold text-ink">
                      <UserRound className="h-3.5 w-3.5 text-muted-ink" />{r.forced_by_user_name || "—"}
                    </div>
                    <div className="text-[11px] text-muted-ink">{fmtDateTime(r.forced_at)}</div>
                  </TableCell>
                  <TableCell className="max-w-[240px] text-[11px] text-muted-ink">
                    {r.forced_reason || "—"}
                  </TableCell>
                  <TableCell><ResolutionBadge resolution={r.resolution} /></TableCell>
                  <TableCell className="max-w-[220px] text-[11px]">
                    {r.resolution === "validated" && (
                      <div>
                        <div className="font-semibold text-ink">{r.validated_by_name || "—"}</div>
                        <div className="text-muted-ink">{fmtDateTime(r.validated_at)}</div>
                      </div>
                    )}
                    {r.resolution === "refused" && (
                      <div>
                        <div className="font-semibold text-ink">{r.refused_by_name || "—"}</div>
                        <div className="text-muted-ink">{fmtDateTime(r.refused_at)}</div>
                        {r.refused_reason && <div className="mt-0.5 italic text-[#9B2226]">« {r.refused_reason} »</div>}
                      </div>
                    )}
                    {r.resolution === "pending" && <span className="text-muted-ink">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-ink">
        <ScrollText className="h-3.5 w-3.5" />
        « Refuser » depuis le planning archive l'exception ici (le créneau est retiré du planning mais conservé pour l'audit).
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registre 2 — Heures (admin) : saisies rétroactives / corrections / annulations
// ---------------------------------------------------------------------------
const HEURES_TABS = [
  { key: "all", label: "Toutes", countKey: "all" },
  { key: "saisie_retroactive", label: "Saisies rétroactives", countKey: "saisie_retroactive" },
  { key: "correction", label: "Corrections", countKey: "correction" },
  { key: "annulation", label: "Annulations", countKey: "annulation" },
];

const HEURES_TYPE = {
  saisie_retroactive: { label: "Saisie rétroactive", cls: "border-[rgba(93,63,211,0.4)] bg-[rgba(93,63,211,0.10)] text-[#5D3FD3]", Icon: CalendarPlus },
  correction: { label: "Correction", cls: "border-[rgba(185,137,0,0.4)] bg-[rgba(185,137,0,0.12)] text-[#8a6600]", Icon: Pencil },
  annulation: { label: "Annulation", cls: "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] text-[#9B2226]", Icon: Ban },
};

function HeuresTypeBadge({ type }) {
  const t = HEURES_TYPE[type] || HEURES_TYPE.correction;
  const Icon = t.Icon;
  return (
    <span data-testid={`heures-type-${type}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${t.cls}`}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

function HeuresJournal() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ all: 0, saisie_retroactive: 0, correction: 0, annulation: 0 });
  const [moisGeles, setMoisGeles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [mois, setMois] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pointage/heures-journal${mois ? `?mois=${mois}` : ""}`);
      setRows(data.rows || []);
      setCounts(data.counts || { all: 0, saisie_retroactive: 0, correction: 0, annulation: 0 });
      setMoisGeles(data.mois_geles || []);
    } catch (_) {
      setRows([]);
    } finally { setLoading(false); }
  }, [mois]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.type === tab)),
    [rows, tab]
  );

  return (
    <div data-testid="heures-journal">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="heures-journal-tabs">
          {HEURES_TABS.map((t) => {
            const active = tab === t.key;
            const n = counts[t.countKey];
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`heures-tab-${t.key}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? "border-[var(--shariti-ink)] bg-[var(--shariti-ink)] text-white"
                    : "border-[var(--border-soft)] bg-white/70 text-ink hover:bg-white"}`}>
                {t.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? "bg-white/25" : "bg-[var(--surface-2)]"}`}>
                  {n ?? 0}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)}
            className="h-9 w-[170px] font-semibold" data-testid="heures-journal-month"
            placeholder="Tous les mois" />
          {mois && (
            <Button variant="ghost" size="sm" onClick={() => setMois("")} data-testid="heures-journal-month-clear"
              className="h-9 px-2 text-xs">Tous</Button>
          )}
          <Button variant="outline" onClick={load} data-testid="heures-journal-refresh" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </Button>
        </div>
      </div>

      {moisGeles.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[rgba(47,111,237,0.25)] bg-[rgba(47,111,237,0.06)] px-3 py-2 text-xs text-[#1E4FB0]"
          data-testid="heures-journal-freeze-banner">
          <Lock className="h-3.5 w-3.5" />
          <span className="font-semibold">Mois gelés (transmis en paie, non modifiables) :</span>
          {moisGeles.map((m) => (
            <span key={m} className="rounded bg-white/70 px-1.5 py-0.5 font-bold">{m}</span>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]">
        {loading ? (
          <div className="space-y-2 p-4" data-testid="heures-journal-loading">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center" data-testid="heures-journal-empty">
            <ClipboardList className="h-8 w-8 text-muted-ink" />
            <p className="text-sm font-semibold text-ink">Aucune opération admin sur les heures</p>
            <p className="text-xs text-muted-ink">Les saisies rétroactives, corrections et annulations apparaîtront ici (qui, quand, motif).</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="heures-journal-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Collaborateur</TableHead>
                  <TableHead>Opération</TableHead>
                  <TableHead>Avant → Après (retenues)</TableHead>
                  <TableHead>Motif</TableHead>
                  <TableHead>Par</TableHead>
                  <TableHead>Le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.pointage_id} data-testid={`heures-journal-row-${r.code_rd}`}>
                    <TableCell className="whitespace-nowrap text-[12px] font-semibold">
                      {frenchDate(r.date)}
                      {r.mois_gele && (
                        <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-[rgba(47,111,237,0.12)] px-1 py-0.5 text-[9px] font-bold text-[#1E4FB0]"
                          data-testid={`heures-journal-gele-${r.code_rd}`}>
                          <Lock className="h-2.5 w-2.5" />gelé
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-[12px]">
                      <div className="font-semibold text-ink">
                        {r.rd_name || "—"}
                        {r.population === "independant" && (
                          <span className="ml-1 rounded bg-[rgba(93,63,211,0.12)] px-1 text-[9px] font-bold uppercase text-[#5D3FD3]"
                            data-testid={`heures-journal-indep-${r.code_rd}`}>Indé</span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-muted-ink">{r.code_rd || "—"}</div>
                    </TableCell>
                    <TableCell><HeuresTypeBadge type={r.type} /></TableCell>
                    <TableCell className="whitespace-nowrap text-[12px]">
                      <div className="inline-flex items-center gap-1.5 tabular-nums">
                        <span className="text-muted-ink">{fmtH(r.avant?.heures)}</span>
                        <ArrowRight className="h-3 w-3 text-muted-ink" />
                        <b className={r.type === "annulation" ? "text-[#9B2226]" : "text-ink"}>
                          {r.type === "annulation" ? "0,00 h" : fmtH(r.apres?.heures)}
                        </b>
                      </div>
                      <div className="mt-0.5 text-[10px] text-muted-ink">
                        {(r.avant?.arrivee || r.apres?.arrivee)
                          ? `${r.avant?.arrivee || "—"}–${r.avant?.depart || "—"} → ${r.apres?.arrivee || "—"}–${r.apres?.depart || "—"}`
                          : ""}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[260px] text-[11px] text-muted-ink">
                      <span className="line-clamp-2">{r.motif || "—"}</span>
                    </TableCell>
                    <TableCell className="text-[12px]">
                      <div className="inline-flex items-center gap-1 font-semibold text-ink">
                        <UserRound className="h-3.5 w-3.5 text-muted-ink" />{r.par || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-[11px] text-muted-ink">
                      {r.le ? frenchDateTime(r.le) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-ink">
        <ScrollText className="h-3.5 w-3.5" />
        Le pointage brut GPS reste immuable : une correction s'affiche en surcouche (valeur retenue) et une annulation
        conserve la ligne (0 h en paie). Toute opération est tracée : auteur, horodatage et motif.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page — bascule entre les deux registres
// ---------------------------------------------------------------------------
export default function ExceptionsJournal() {
  const [mode, setMode] = useState("rebookings");

  return (
    <div className="min-h-screen bg-[var(--surface-1)]">
      <PageHeader
        title="Journal des exceptions"
        subtitle="Audit permanent — rebookings forcés du planning et opérations admin sur les heures (saisies rétroactives, corrections, annulations)."
      >
        <div className="flex flex-wrap gap-2" data-testid="journal-mode-switch">
          {MODES.map((m) => {
            const active = mode === m.key;
            const Icon = m.Icon;
            return (
              <button key={m.key} onClick={() => setMode(m.key)}
                data-testid={`journal-mode-${m.key}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                  active
                    ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white"
                    : "border-[var(--border-soft)] bg-white/70 text-ink hover:bg-white"}`}>
                <Icon className="h-4 w-4" /> {m.label}
              </button>
            );
          })}
        </div>
      </PageHeader>

      <div className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8">
        {mode === "rebookings" ? <RebookingsJournal /> : <HeuresJournal />}
      </div>
    </div>
  );
}
